import React, { useMemo } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { filter, map, orderBy, times } from "lodash";
import { Icon, IconName } from "@/shared/ui/Icon";
import { SortArrows } from "@/shared/ui/SortArrows";
import { Column, Row } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";
import { Pagination, PaginationProps } from "./Pagination";
import TableRow from "@/shared/ui/TableRow";
import { Sort } from "@/shared/utils/hooks/useSortAndPagination";
import { Skeleton } from "@/shared/ui/Skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import MarkdownText from "../MarkdownText";

interface TableColumn<T> {
  className?: string;
  title: string;
  span?: number | "auto";
  accessor?: (data: T) => React.ReactNode | string | undefined;
  sortable?: boolean;
  sortDirection?: "asc" | "desc";
  icon?: IconName;
  hidden?: boolean;
  tooltipText?: string; // Markdown text to display in a tooltip in column header. If not provided, the column header will not have a tooltip.
}

export interface TableProps<T extends {}>
  extends PaginationProps,
    VariantProps<typeof tableVariants> {
  className?: string;
  columns: Record<string, TableColumn<T>>;
  data: T[];
  visible?: boolean;
  loading?: boolean;
  onSort?: (sort: Sort) => void;
  onRowClick?: (row: T) => void;
}

const tableVariants = cva("rounded-md overflow-x-scroll", {
  variants: {
    variant: {
      bordered: "border border-solid border-grey-300",
      ghost: "",
    },
  },
  defaultVariants: {
    variant: "bordered",
  },
});

function TableLoader({
  columns,
  variant,
}: {
  columns: number;
  variant?: string;
}) {
  return (
    <>
      {times(5, (rowIndex) => (
        <TableRow
          key={rowIndex}
          variant={variant === "ghost" ? "ghost" : "body"}
        >
          {times(columns, (colIndex) => (
            <td
              key={colIndex}
              className={cn([
                "align-middle",
                variant !== "ghost" && "first:pl-4 pr-8 last:pr-4",
              ])}
            >
              <Skeleton
                className={cn(
                  "h-6 bg-grey-200",
                  colIndex === 0 ? "w-3/4" : "w-full"
                )}
              />
            </td>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function Table<T extends { [key: string]: any }>(props: TableProps<T>) {
  const {
    columns,
    data,
    className,
    visible = true,
    loading = false,
    onSort,
    onRowClick,
    variant,
  } = props;

  if (!visible) return null;

  const visibleColumns = useMemo(() => {
    const columnsList = map(columns, (column, key) => ({
      ...column,
      key,
    }));

    return orderBy(
      filter(columnsList, (column) => !column.hidden),
      "order"
    );
  }, [columns]);

  const getColumnWidth = (column: TableColumn<T>) => {
    const { span } = column;
    if (span === 0) return "45px";
    if (!span || span === "auto") return "auto";

    return `${span * 10}%`;
  };

  const getColumnValue = (key: string, row: T, column: TableColumn<T>) => {
    if (column.accessor) {
      return column.accessor(row);
    }

    return row[key as any];
  };

  const tableRows = data.map((row, index) => (
    <TableRow
      key={`${row.id || row.title}-${index}-${row.name}`}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      variant={variant === "ghost" ? "ghost" : "body"}
    >
      {map(visibleColumns, (column) => (
        <td
          key={`${row.id || row.title}-${index}-${row.name}-${column.key}`}
          className={cn([
            "align-middle whitespace-nowrap",
            variant !== "ghost" && "first:pl-4 pr-8 last:pr-4",
            // hoverableStyles,
            column.className,
          ])}
        >
          {getColumnValue(column.key, row, column)}
        </td>
      ))}
    </TableRow>
  ));

  const tableHeaders = map(visibleColumns, (column, index) => {
    const width = getColumnWidth(column);

    return (
      <th
        style={{ width }}
        key={column.key}
        className={cn("align-middle pr-4", variant !== "ghost" && "first:pl-4")}
      >
        <Row
          className={cn([
            "items-center space-x-2",
            column.sortable && "cursor-pointer",
            column.className,
          ])}
          onClick={() => {
            column.sortable &&
              onSort &&
              onSort({
                key: column.key,
                direction: column.sortDirection === "asc" ? "desc" : "asc",
              });
          }}
        >
          {column.icon && <Icon size="sm" icon={column.icon} />}
          <p className="text-grey-700 font-semibold text-sm">{column.title}</p>
          {column.tooltipText && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Icon icon="Info" size="sm" color="primary" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-white max-w-[320px] md:max-w-[480px]"
              >
                <MarkdownText
                  className="prose prose-p:text-sm prose-ul:text-sm"
                  text={column.tooltipText}
                />
              </TooltipContent>
            </Tooltip>
          )}
          {column.sortable && (
            <SortArrows
              sort={column.sortDirection}
              onSort={(dir) => {
                onSort &&
                  onSort({
                    key: column.key,
                    direction: dir,
                  });
              }}
            />
          )}
        </Row>
      </th>
    );
  });

  return (
    <>
      <Column className={cn(tableVariants({ variant }), className)}>
        <table className={cn("rounded-md border-spacing-4")}>
          <thead>
            <TableRow variant={variant === "ghost" ? "ghost" : "header"}>
              {tableHeaders}
            </TableRow>
          </thead>
          <tbody>
            {loading ? (
              <TableLoader
                columns={visibleColumns.length}
                variant={variant || "bordered"}
              />
            ) : (
              tableRows
            )}
          </tbody>
        </table>
      </Column>
      <Pagination {...props} />
    </>
  );
}

export { Table };
